import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {
    clearDatasetDetails,
    createDatasetDetails,
    deActivateDataset,
    deleteDataset,
    fetchDatasetDetails,
    publishDataset,
    reActivateDataset,
    updateDatasetDetails
} from "../../../state/api/datasetAPI";
import * as yup from "yup";
import {useForm} from "common/src/hooks/form/formHook";
import FormContainer from "common/src/components/form/FormContainer";
import Input from "common/src/components/widget/Input";
import Checkbox from "common/src/components/widget/Checkbox";
import Button from "common/src/components/widget/Button";
import {DatasetStatus} from "../../../model/datasetStatus";
import ValueLabel from "../../widget/ValueLabel";
import Fragment from "../../../../../common/src/components/widget/Fragment";
import Select from "common/src/components/widget/Select";
import {fetchRepresentation} from "../../../state/api/dataAPI";
import SelectedColumns from "../../widget/query/SelectedColumns";
import AggregationsCard from "../../widget/query/AggregationsCard";
import StaticFilterColumnsCard from "../../widget/query/StaticFilterColumnsCard";
import DynamicFilterColumnsCard from "../../widget/query/DynamicFilterColumnsCard";
import GroupByColumnsCard from "../../widget/query/GroupByColumnsCard";
import {clearInfiniteScroll, fetchGroupPage} from "../../../state/api/groupAPI";
import FloatingLabelTagsWrapper from "common/src/components/widget/FloatingLabelTagsWrapper";
import {addErrorNotification, addSuccessNotification} from "../../../state/notification/notifications";
import ColumnsDescriptionControl from "../../widget/query/ColumnsDescriptionControl";
import ConfirmableButton from "common/src/components/widget/ConfirmableButton";
import {getKey} from "../../../utils/InfiniteScroleUtil";
import Loader from "common/src/components/widget/Loader";
import {constructFilter} from "common/src/utils/AdvancedFilterApiUtils";
import {constructSort} from "common/src/utils/APIUtils";
import {isEqual} from "lodash";
import {UserContext} from "../../../context/UserContext";
import {hasRole} from "../../../utils/userUtils";
import {Role} from "../../../auth/roles";

const DatasetForm = () => {

    const {t} = useTranslation();
    const navigate = useNavigate();
    const {user} = useContext(UserContext);
    const [keyBasedClassNameAdditions, setKeyBasedClassNameAdditions] = useState({});

    const {datasetId} = useParams();
    const edit = typeof (datasetId) !== "undefined";
    const [paramId, setParamId] = useState(datasetId);

    const [isBusy, setIsBusy] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const dataset = useSelector(state => state.datasetAPI[datasetId]);

    const dataRepresentation = useSelector(state => state.dataAPI.representation);
    const dataRepresentationValid_to = useSelector(state => state.dataAPI.valid_to);
    const dataRepresentationLoading = useSelector(state => state.dataAPI.representationLoading);
    const dispatch = useDispatch();

    const [filterString, setFilterString] = useState(getKey(constructFilter([])));
    const [sortString, setSortString] = useState(getKey(constructSort({ name: "asc" })));
    const total = useSelector(state => state.groupAPI.statesInfinitiveScrolls?.[filterString]?.totalElements);
    const totalPages = useSelector(state => state.groupAPI.statesInfinitiveScrolls?.[filterString]?.totalPages);

    const groupOptions = useSelector(state => state.groupAPI.statesInfinitiveScrolls?.[filterString]?.[sortString]?.content);
    const loadingGroups = useSelector(state => state.groupAPI.loading);
    const currentOptionsPage = useRef(-1);
    const optionsFilter = useRef(null);
    const [options, setOptions] = useState([]);
    useEffect(
        () => {
            dispatch(clearInfiniteScroll());
        }, []);
    useEffect(() => {
        if (!loadingGroups) {
            setOptions(groupOptions ?? []);
        }
    }, [total, totalPages, groupOptions, loadingGroups]);

    const onSearchQueryChange = useCallback((query) => {
        if (!isEqual(query, optionsFilter.current)) {
            optionsFilter.current = query;
            currentOptionsPage.current = 0;
        }

        const filter = query && query.trim() !== "" ? [{
            name: "name",
            operator: "LIKE_IC",
            value: `${query.trim()}`
        }] : [];
        const filterStringInternal = getKey(constructFilter(filter));
        const sortString = getKey(constructSort({ "name": "asc" }));
        setSortString(sortString);

        if (filterStringInternal && filterStringInternal !== "") {
            setFilterString(filterStringInternal);
        } else {
            setFilterString("blank");
        }
        const pageMod = totalPages || 1;
        console.log("pageMod", pageMod);
        dispatch(fetchGroupPage({
            page: {
                page: (currentOptionsPage.current % pageMod),
                size: 10,
            },
            sort: { "name": "asc" },
            filter
        }));
    }, [totalPages]);

    const onFetchNextPage = () => {
        if (!loadingGroups) {
            currentOptionsPage.current += 1;
            onSearchQueryChange(optionsFilter.current);
        }
    };

    useEffect(() => {
        if (!dataRepresentation || dataRepresentation.length === 0) {
            !dataRepresentationLoading && dispatch(fetchRepresentation());
        }
        return () => {

        };
    }, [dataRepresentationLoading, dataRepresentation, dataRepresentationValid_to]);
    useEffect(() => {
        !!datasetId && dataset?.id !== datasetId && setIsLoading(true);
        !!datasetId && dispatch(fetchDatasetDetails(datasetId));
        }, [datasetId]);
    useEffect(() => {
        if (paramId !== datasetId && !edit) {
            navigate(`/dataset/edit/${paramId}`);
        }
        return () => {}
        }, [paramId, datasetId]);

    useEffect(() => {
        if (dataset) {
            setValues({...dataset});
            if (isLoading) {
                setIsLoading(false);
            }
        }

        return () => {
            setFormNotDirty(true);
            dispatch(clearDatasetDetails());
        }
    }, [dataset]);
    const schema = useMemo(() => {
        return {
            name: yup.string().max(255).required(),
            description: yup.string().max(255).required(),
            isPublic: yup.bool().required(),
            query: yup.object().shape({
                baseDataTable: yup.string().required(t('validation.required.baseDataTable') ?? 'Table name is required'),
            })
        }
    }, [t]);

    const submit = async () => {
        if (!hasRole(user, Role.DATASET_CREATE)) {
            dispatch(addErrorNotification({
                title: t('toast.error'),
                content: t('toast.insufficient_permissions')
            }));
            return;
        }
        const successMessage = t(`toast.success_${edit ? 'edit' : 'add'}_dataset`);
        const dataset = {...values};
        setIsBusy(true);
        if (datasetId) {


            await dispatch(updateDatasetDetails({datasetId: datasetId, dto: dataset}))
                .unwrap()
                .then(
                    (data) => {
                        setFormNotDirty(true);
                        setValues(data);
                        dispatch(addSuccessNotification({title: t('toast.success'), content: successMessage}));
                        // navigate(`/dataset/edit/${data.id}`);
                        isBusy && setIsBusy(false);
                    }
                ).catch(
                    (error) => {
                        console.error(error);
                        dispatch(addErrorNotification(
                            {
                                title: t(`toast.error`),
                                content: t(`toast.error_${edit ? 'edit' : 'add'}_dataset`) + "\n" + error.message
                            }));
                    }
                ).finally(
                    () => {
                        setIsBusy(false);
                    }
                )

        } else {
            dispatch(createDatasetDetails(dataset)).unwrap()
                .then(
                    (data) => {

                        setFormNotDirty(true);
                        setValues(data);
                        dispatch(addSuccessNotification({title: t('toast.success'), content: successMessage}));

                        isBusy && setIsBusy(false);
                        setParamId(data.id);

                        return data;
                    }
                ).catch(
                (error) => {
                    dispatch(addErrorNotification(
                        {
                            title: t(`toast.error`),
                            content: t(`toast.error_${edit ? 'edit' : 'add'}_dataset`) + "\n" + error.message
                        }));
                    console.error(error);
                }
            ).finally(
                () => {
                    setIsBusy(false);
                }
            )
        }
    }

    const {
        values,
        getField,
        handleSubmit,
        setValues,
        isEdited,
        setFormNotDirty
    } = useForm(submit, dataset || {
        name: "",
        description: "",
        isPublic: false,
        groups: [],
        query: {
            baseDataTable: "",
            selectedColumns: [],
            aggregations: [],
            numericFilters: [],
            stringFilters: [],
            timestampRangeFilters: [],
            groupByColumns: [],
            timestampFilters: []
        },
        columnDescription: {}

    }, schema, onInvalid);

    function onInvalid() {
        dispatch(addErrorNotification(
            {
                title: t(`toast.validation.error`),
                content: t(`toast.form_validation.error_${edit ? 'edit' : 'add'}_dataset`)
            }
        ));
    }

    const findColumnOptions = () => {
        return dataRepresentation.find(d => d.entityName === values?.query?.baseDataTable)?.fields ?? [];
    }

    const canEditAccessLevelForDatasetDataset = () => {
        return values?.status === undefined ||
            values?.status === null || values?.status === 'DRAFT' || values?.status === 'PUBLISHED'
    }
    const isValidQuery = (values) => {
        return (values?.query?.selectedColumns?.length > 0 ||
            values?.query?.aggregations?.length > 0) && values?.query?.baseDataTable !== "";
    }
    const shouldCheckIntentionalRedundantGroupByFields = () => {
        return (values?.query?.groupByColumns?.length > 0 &&
            (values?.query?.groupByColumns
                    .some(it => values?.query?.selectedColumns?.find(col => col.name === it) === undefined)
                ||
                values?.query?.selectedColumns?.length === 0
            )
        )
    }
    const isEditDisabled = () => {
        // The query/filters are editable regardless of DRAFT/PUBLISHED status.
        return false;
    }

    const isBaseTableSelectDisabled = useCallback(() => {
        return values?.query?.selectedColumns?.length > 0 ||
            values?.query?.aggregations?.length > 0 ||
            values?.query?.groupByColumns?.length > 0 ||
            values?.query?.numericFilters?.length > 0 ||
            values?.query?.stringFilters?.length > 0 ||
            values?.query?.timestampRangeFilters?.length > 0 ||
            values?.query?.timestampFilters?.length > 0
    }, [values?.query]);

    const handleGroupChange = (group, isSelected) => {

        if (isSelected) {
            const valueTemp = [...(getField("groups").value ?? []), {...group}] ?? [];
            getField("groups").setValue([...valueTemp], null, true);
            setValues({...values, "groups": [...valueTemp]})

        } else {

            const valueTemp =
                [...[...getField("groups").value].filter(item => item.id !== group.id)] ?? [];

            getField("groups").setValue([...valueTemp], null, true);
            setValues({...values, "groups": [...valueTemp]})
        }

    }

    const handleTagRemove = (tagToRemove) => {
        handleGroupChange(tagToRemove, false)
    };

    if ( isLoading) {
        return <><Loader /></>
    }

    function handleDeleteDataset() {
        if (!hasRole(user, Role.DATASET_DELETE)) {
            dispatch(addErrorNotification({
                title: t('toast.error'),
                content: t('toast.insufficient_permissions')
            }));
            return;
        }
        return dispatch(deleteDataset(datasetId ?? values.id))
            .unwrap()
            .then(
                _ => {
                    dispatch(addSuccessNotification({
                        title: t('toast.success_delete_dataset'),
                        content: t('toast.success')
                    }));
                    navigate("/dataset");
                }
            )
            .catch(err => {
                dispatch(addErrorNotification({
                    title: t('toast.error_delete_dataset'),
                    content: t(err?.message ?? 'toast.failed')
                }));
            })
    }

    function handleDeactivateDataset() {
        if (!hasRole(user, Role.DATASET_PUBLISH)) {
            dispatch(addErrorNotification({
                title: t('toast.error'),
                content: t('toast.insufficient_permissions')
            }));
            return;
        }
        dispatch(deActivateDataset(datasetId ?? values.id))
            .unwrap()
            .then((dataset) => {
                dispatch(addSuccessNotification({
                    title: t('toast.success_deactivate_dataset'),
                    content: t('toast.success')
                }));
                const valuesCopy = {...values};
                valuesCopy.isActive = false;
                console.log("valuesCopy options", valuesCopy, values, dataset, {...dataset, ...valuesCopy}, {...values, ...dataset});
                setFormNotDirty(true);
                setValues({...dataset});
            })
            .catch(err => {
                dispatch(addErrorNotification({
                    title: t('toast.error_deactivate_dataset'),
                    content: t(err?.message ?? 'toast.failed')
                }));
            })
    }

    function handleReActivateDataset() {
        if (!hasRole(user, Role.DATASET_PUBLISH)) {
            dispatch(addErrorNotification({
                title: t('toast.error'),
                content: t('toast.insufficient_permissions')
            }));
            return;
        }
        if (!values.id) {
            console.warn("No dataset id found, cannot reactivate dataset!");
            return;
        }
        if (values.status !== 'PUBLISHED') {
            console.warn("Dataset is not published, cannot reactivate dataset!");
            return;
        }
        if (values.isActive) {
            console.warn("Dataset is active, cannot reactivate dataset!");
            return;
        }
        dispatch(reActivateDataset(datasetId ?? values.id)).unwrap()
            .then((dataset) => {
                dispatch(addSuccessNotification({
                    content: t('toast.success_reActivate_dataset'),
                    title: t('toast.success')
                }));
            })
            .catch(err => {
                dispatch(fetchDatasetDetails(values.id));
            })
    }


    function handlePublishDataset() {
        if (!hasRole(user, Role.DATASET_PUBLISH)) {
            dispatch(addErrorNotification({
                title: t('toast.error'),
                content: t('toast.insufficient_permissions')
            }));
            return;
        }
        setIsBusy(true);
        dispatch(fetchDatasetDetails(values.id)).unwrap()
            .then((dataset) => {
                if (!isValidQuery(dataset)) {
                    dispatch(addErrorNotification({
                        title: t('toast.error_publish_dataset'),
                        content: t('toast.error_no_query_selections')
                    }));
                    setIsBusy(false);
                } else {
                    dispatch(publishDataset(datasetId ?? values.id)).unwrap()
                        .then((dataset) => {
                            dispatch(addSuccessNotification({
                                title: t('toast.success_publish_dataset'),
                                content: t('toast.success')
                            }));
                        })
                        .catch(err => {
                            dispatch(addErrorNotification({
                                title: t('toast.error_publish_dataset'),
                                content: t(err?.message ?? 'toast.failed')
                            }));
                        }).finally(() => {
                        setIsBusy(false);
                    })
                }
            });
    }

    const togglerKey = (key, className) => {
        setKeyBasedClassNameAdditions(prevState => {
            const updatedClasses = {...prevState};

            if (updatedClasses[key] && updatedClasses[key].includes(className)) {
                // Remove class if it exists
                updatedClasses[key] = updatedClasses[key].replace(className, '').trim();
                if (updatedClasses[key] === '') {
                    delete updatedClasses[key]; // Remove the key if no classes left
                }
            } else {
                // Add class if it doesn't exist
                updatedClasses[key] = (updatedClasses[key] || '') + ` ${className}`.trim();
            }

            return updatedClasses;
        });
    };
    return (
        <FormContainer onSubmit={handleSubmit} isEdited={isEdited}>
            <div className="card">
                <header className="card-header">
                    <>
                        <p className="card-header-title">
                            <span className="icon fz fz-binoculars medium mr-1"/>
                            <span>{t('drawer.dataset')}</span>
                        </p>
                        <a
                            className="dropdown-indicator right-decoration left"
                            onClick={() => togglerKey('metadata', 'is-hidden')}
                        >
                            <i
                                className={`fz fz-chevron-${
                                    keyBasedClassNameAdditions['metadata']?.includes('is-hidden') ? "down" : "left"
                                }`}
                            ></i>
                        </a>
                    </>
                </header>
                <div
                    className={`card-content collapse-content ${keyBasedClassNameAdditions['metadata'] || ''}`}
                >
                    <div className="columns">
                        <div className="column is-half">
                            <Input label={t('dataset.name')} formField={getField("name")}
                                   autoComplete={"new-password"}
                                   disabled={!canEditAccessLevelForDatasetDataset()}/>
                            <Input label={t('dataset.description')} formField={getField("description")}
                                   autoComplete={"new-password"}
                                   disabled={!canEditAccessLevelForDatasetDataset()}/>

                            <div className="columns">
                                <div className="column">
                                    <Fragment rendered={datasetId !== undefined}>
                                        <ValueLabel label={t('dataset.status')}
                                                    value={t(DatasetStatus[values.status])}/>
                                    </Fragment>
                                    <ValueLabel label={t('dataset.active')}
                                                checked={values?.isActive !== undefined ? values?.isActive : true}
                                                isCheckbox={true}/>
                                </div>
                                <div className="column">
                                    <Checkbox label={t('dataset.public')}
                                              formField={getField('isPublic')}
                                              disabled={!canEditAccessLevelForDatasetDataset()}
                                    />
                                </div>

                            </div>

                        </div>
                        <div className="column is-one-quarter-tablet">
                            <>

                                <div className="column">
                                    <Select label={t('label.dataset.add_groups')}
                                            isMultiSelect={true}
                                            formField={getField("groups")}
                                            onSearchQueryChange={onSearchQueryChange}
                                            loading={loadingGroups}
                                            fetchMoreOptions={onFetchNextPage}
                                            hasMoreOptions={(total == null || (total > 0 && (total > options.length)))}
                                            options={[...(options || [])]}
                                            optionsCode={"id"}
                                            optionsValue={"id"}
                                            optionsLabel={"name"}
                                            isSelectionAnObject={true}
                                            emptyOption={false}/>
                                </div>
                            </>

                        </div>
                        <div className="column is-one-quarter-tablet">
                            {
                                getField("groups") && getField("groups").value &&
                                <FloatingLabelTagsWrapper
                                    label="Groups"
                                    tags={[...getField("groups").value]}
                                    onTagRemove={handleTagRemove}
                                    tooltipFormatter={(tag) => tag.description}
                                    disabled={!canEditAccessLevelForDatasetDataset()}

                                />
                            }
                        </div>

                    </div>
                </div>
            </div>

            <Fragment rendered={true}>
                <>
                    <div className="card">
                        <header className="card-header">
                            <p className="card-header-title">
                                <span className="icon fz fz-list-edit medium mr-1"/>
                                <span>{t('dataset.tableAndColumnDescriptions')} </span>

                            </p>
                            <a
                                className="dropdown-indicator right-decoration left"
                                onClick={() => togglerKey('dataset.table', 'is-hidden')}
                            >
                                <i
                                    className={`fz fz-chevron-${
                                        keyBasedClassNameAdditions['dataset.table']?.includes('is-hidden') ? "down" : "left"
                                    }`}
                                ></i>
                            </a>
                        </header>
                        <div
                            className={`card-content collapse-content ${keyBasedClassNameAdditions['dataset.table'] || ''}`}
                        >
                            <div>
                                <Select options={dataRepresentation ? dataRepresentation : []}
                                        optionsCode={"entityName"}
                                        optionsLabel={"entityName"}
                                        optionsValue={"entityName"}
                                        label={t('data.table_name')}
                                        formField={getField("query.baseDataTable")}
                                        disabled={isBaseTableSelectDisabled()}
                                />
                                <ColumnsDescriptionControl
                                    aggregations={getField("query.aggregations")}
                                    selectedColumns={getField("query.selectedColumns")}
                                    columnDescriptionFormField={getField("columnDescription")}
                                    disabled={isEditDisabled()}/>
                            </div>
                        </div>
                    </div>
                </>

            </Fragment>
            {
                (!isEditDisabled() ||
                    values?.query?.selectedColumns?.length !== 0 ||
                    getField("query.selectedColumns").value.length > 0) &&
                <div className="card">
                    <header className="card-header">
                        <p className="card-header-title">
                            <span className="icon fz fz-list-edit medium mr-1"/>
                            <span>{t('dataset.fields')}</span>
                        </p>
                        <a
                            className="dropdown-indicator right-decoration left"
                            onClick={() => togglerKey('dataset.fields', 'is-hidden')}
                        >
                            <i
                                className={`fz fz-chevron-${
                                    keyBasedClassNameAdditions['dataset.fields']?.includes('is-hidden') ? "down" : "left"
                                }`}
                            ></i>
                        </a>
                    </header>
                    <div
                        className={`card-content collapse-content ${keyBasedClassNameAdditions['dataset.fields'] || ''}`}
                    >

                        <SelectedColumns
                            options={findColumnOptions()}
                            formField={getField("query.selectedColumns")}
                            disabled={isEditDisabled()}
                            restrictedLabels={getField("query.aggregations").value?.map((agg) => agg.label) || []}
                        />

                    </div>
                </div>
            }
            <Fragment rendered={
                ((

                    !isEditDisabled() || values?.query?.aggregations?.length !== 0))}>
                <AggregationsCard
                    options={findColumnOptions()}
                    formField={getField("query.aggregations")}
                    disabled={isEditDisabled()}
                    restrictedLabels={getField("query.selectedColumns").value?.map((col) => col.label) || []}
                />
            </Fragment>


            <Fragment rendered={
                (
                    (!isEditDisabled() ||
                        (values.query?.numericFilters?.length !== 0 ||
                            values.query?.stringFilters?.length !== 0 ||
                            values.query?.timestampRangeFilters?.length !== 0 ||
                            values.query?.timestampFilters?.length !== 0
                        )
                    ))}>
                <StaticFilterColumnsCard
                    options={findColumnOptions()}
                    numericFilterFormField={getField("query.numericFilters")}
                    stringFilterFormField={getField("query.stringFilters")}
                    timeRangeFilterFormField={getField("query.timestampRangeFilters")}
                    timestampFilterFormField={getField("query.timestampFilters")}
                    disabled={isEditDisabled()}/>
                <DynamicFilterColumnsCard
                    options={findColumnOptions()}
                    numericFilterFormField={getField("query.numericFilters")}
                    stringFilterFormField={getField("query.stringFilters")}
                    timeRangeFilterFormField={getField("query.timestampRangeFilters")}
                    timestampFilterFormField={getField("query.timestampFilters")}
                    disabled={isEditDisabled()}/>
            </Fragment>

            <Fragment rendered={
                (
                    (!isEditDisabled() || values.query?.groupByColumns?.length !== 0))}>
                <GroupByColumnsCard formField={getField("query.groupByColumns")}
                                    aggregatedColumns={values?.query?.aggregations ?? []}
                                    selectedColumns={values?.query?.selectedColumns ?? []}
                                    options={findColumnOptions()}
                                    disabled={isEditDisabled()}/>
            </Fragment>
            <Fragment rendered={!isEditDisabled() || canEditAccessLevelForDatasetDataset()}>
                <ConfirmableButton
                    isSubmit={false}
                    onClick={() => handleSubmit()}
                    color="primary"
                    shouldConfirm={shouldCheckIntentionalRedundantGroupByFields() && isEdited() && !isEditDisabled()}
                    confirmMessage={t('dataset.group_by_redundancies')}
                    label={datasetId ? t('save') : t('submit')}
                    isLoading={isBusy}
                    disabled={isBusy || isLoading}/>
            </Fragment>
            <Button color="primary" isOutlined={true} label={t('cancel')} onClick={() => navigate("/dataset")}/>
            <Fragment
                rendered={values &&
                    values.id &&
                    (values.status === 'DRAFT')
                }>
                <ConfirmableButton onClick={() => handlePublishDataset()}
                                   confirmMessage={t('dataset.publish_confirm_unsaved_changes')}
                                   shouldConfirm={isEdited()}
                                   color="warning"
                                   isLoading={isBusy}
                                   disabled={isBusy}
                                   label={t('dataset.publish')}/>
            </Fragment>

            <Fragment
                rendered={(datasetId !== undefined || values.id) && (!values.isActive && values.status === 'PUBLISHED')
                    && !!values.id}>
                <ConfirmableButton onClick={handleReActivateDataset}
                                   color="warning"
                                   shouldConfirm={isEdited()}
                                   confirmMessage={t('dataset.publish_confirm_unsaved_changes')}
                                   isLoading={isBusy}
                                   label={t('dataset.Activate')}/>
            </Fragment>
            <Fragment
                rendered={(datasetId !== undefined || values.id) && values.isActive && values.status === 'PUBLISHED'}>
                <Button onClick={() => handleDeactivateDataset()}
                        color="warning"
                        label={t('dataset.deactivate')}
                        className={"is-outlined"}/>
            </Fragment>
            <Fragment rendered={(datasetId !== undefined || values.id) && !values.isActive || values.status === 'DRAFT'}>
                <Button onClick={() => handleDeleteDataset()} color="danger"
                        label={t('delete')} className={"is-outlined"}/>
            </Fragment>
        </FormContainer>
    )
}

export default DatasetForm;
